/** Default Fib retracement coefficients — keep in sync with FibonacciDrawingTool.sectionLevels. */
export const FIBONACCI_RETRACEMENT_LEVELS = [
    0,
    0.236,
    0.382,
    0.5,
    0.618,
    0.786,
    1,
    1.618,
    2.618,
    3.618,
    4.236,
] as const

/** Scalar (Crossing / GT / LT) selected level. */
export const FIBONACCI_ALERT_LEVEL_OPTION_KEY = 'level'
/** Channel upper bound by price (higher price). */
export const FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY = 'upperLevel'
/** Channel lower bound by price (lower price). */
export const FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY = 'lowerLevel'

export const DEFAULT_FIBONACCI_ALERT_LEVEL = '0.5'
export const DEFAULT_FIBONACCI_ALERT_UPPER_LEVEL = '0.5'
export const DEFAULT_FIBONACCI_ALERT_LOWER_LEVEL = '0.618'

/** price(coeff) = p2 - (p2 - p1) * coeff — matches FibonacciDrawingTool.calculateSections. */
export function getFibonacciLevelPrice(price1: number, price2: number, level: number): number {
    return price2 - (price2 - price1) * level
}

export function parseFibonacciAlertLevel(
    raw: string | undefined | null,
    fallback: string = DEFAULT_FIBONACCI_ALERT_LEVEL
): number {
    const n = Number(raw)
    if (!Number.isFinite(n)) return Number(fallback)
    return n
}

export function formatFibonacciLevelLabel(level: number, price?: number): string {
    const levelText = Number.isInteger(level) ? String(level) : String(level)
    if (price == null || !Number.isFinite(price)) return levelText
    return `${levelText} (${price.toFixed(2)})`
}

export function getFibonacciLevelIndex(level: number, levels: readonly number[] = FIBONACCI_RETRACEMENT_LEVELS): number {
    const exact = levels.findIndex((l) => Math.abs(l - level) < 1e-9)
    if (exact >= 0) return exact
    let nearest = 0
    let best = Number.POSITIVE_INFINITY
    levels.forEach((l, i) => {
        const d = Math.abs(l - level)
        if (d < best) {
            best = d
            nearest = i
        }
    })
    return nearest
}

/** Fallback adjacent pair when only a single level is known. */
export function getAdjacentFibonacciLevelPair(
    selectedLevel: number,
    levels: readonly number[] = FIBONACCI_RETRACEMENT_LEVELS
): { levelA: number; levelB: number } {
    const idx = getFibonacciLevelIndex(selectedLevel, levels)
    if (idx < levels.length - 1) {
        return { levelA: levels[idx], levelB: levels[idx + 1] }
    }
    if (idx > 0) {
        return { levelA: levels[idx - 1], levelB: levels[idx] }
    }
    return { levelA: levels[0], levelB: levels[Math.min(1, levels.length - 1)] }
}

export function getFibonacciAlertLevelFromDrawing(options: Record<string, string> | undefined | null): string {
    const raw = options?.[FIBONACCI_ALERT_LEVEL_OPTION_KEY]
    if (raw == null || raw === '') return DEFAULT_FIBONACCI_ALERT_LEVEL
    return String(raw)
}

/**
 * Resolve channel bound coefficients from drawing options.
 * Upper/lower are by price: upperLevel produces the higher price, lowerLevel the lower price.
 */
export function getFibonacciChannelBoundLevelsFromOptions(
    options: Record<string, string> | undefined | null,
    price1?: number,
    price2?: number
): { upperLevel: number; lowerLevel: number } {
    const hasUpper = options?.[FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY] != null && options[FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY] !== ''
    const hasLower = options?.[FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY] != null && options[FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY] !== ''

    let upperLevel = parseFibonacciAlertLevel(
        options?.[FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY],
        DEFAULT_FIBONACCI_ALERT_UPPER_LEVEL
    )
    let lowerLevel = parseFibonacciAlertLevel(
        options?.[FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY],
        DEFAULT_FIBONACCI_ALERT_LOWER_LEVEL
    )

    if (!hasUpper || !hasLower) {
        const selected = parseFibonacciAlertLevel(getFibonacciAlertLevelFromDrawing(options))
        const { levelA, levelB } = getAdjacentFibonacciLevelPair(selected)
        if (!hasUpper) upperLevel = levelA
        if (!hasLower) lowerLevel = levelB
    }

    // Normalize so "upper" is the higher price when anchors are available.
    if (price1 != null && price2 != null && Number.isFinite(price1) && Number.isFinite(price2)) {
        const priceUpper = getFibonacciLevelPrice(price1, price2, upperLevel)
        const priceLower = getFibonacciLevelPrice(price1, price2, lowerLevel)
        if (priceUpper < priceLower) {
            return { upperLevel: lowerLevel, lowerLevel: upperLevel }
        }
    }

    return { upperLevel, lowerLevel }
}

export function buildFibonacciChannelOptions(
    options: Record<string, string> | undefined | null,
    price1?: number,
    price2?: number
): Record<string, string> {
    const { upperLevel, lowerLevel } = getFibonacciChannelBoundLevelsFromOptions(options, price1, price2)
    return {
        ...(options ?? {}),
        [FIBONACCI_ALERT_UPPER_LEVEL_OPTION_KEY]: String(upperLevel),
        [FIBONACCI_ALERT_LOWER_LEVEL_OPTION_KEY]: String(lowerLevel),
        [FIBONACCI_ALERT_LEVEL_OPTION_KEY]:
            options?.[FIBONACCI_ALERT_LEVEL_OPTION_KEY] ?? DEFAULT_FIBONACCI_ALERT_LEVEL,
    }
}

export type FibonacciChannelBoundsValidation = {
    valid: boolean
    error?: string
    upperPrice?: number
    lowerPrice?: number
}

/** Validate upper/lower Fib levels: distinct coeffs and upper price > lower price. */
export function validateFibonacciChannelBounds(
    price1: number | undefined,
    price2: number | undefined,
    upperLevel: number,
    lowerLevel: number
): FibonacciChannelBoundsValidation {
    if (Math.abs(upperLevel - lowerLevel) < 1e-9) {
        return { valid: false, error: 'Upper and lower bounds must use different Fib levels' }
    }
    if (price1 == null || price2 == null || !Number.isFinite(price1) || !Number.isFinite(price2)) {
        return { valid: true }
    }
    const upperPrice = getFibonacciLevelPrice(price1, price2, upperLevel)
    const lowerPrice = getFibonacciLevelPrice(price1, price2, lowerLevel)
    if (upperPrice <= lowerPrice) {
        return {
            valid: false,
            error: 'Upper bound price must be greater than lower bound price',
            upperPrice,
            lowerPrice,
        }
    }
    return { valid: true, upperPrice, lowerPrice }
}
